type IdleWindow = Window &
  typeof globalThis & {
    requestIdleCallback?: (
      callback: IdleRequestCallback,
      options?: IdleRequestOptions,
    ) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

// GPU initialization includes shader compilation. Keep effects from competing
// with each other while the first screen and hero video are settling.
let effectStartup = Promise.resolve();

export function deferEffectUntilPageSettles(
  task: () => Promise<unknown>,
  delay: number,
  timeout: number,
) {
  let cancelled = false;
  const cancelStart = deferUntilPageSettles(
    () => {
      effectStartup = effectStartup
        .then(async () => {
          if (cancelled) return;
          await new Promise<void>((resolve) =>
            requestAnimationFrame(() => resolve()),
          );
          if (!cancelled) await task();
        })
        .catch(() => undefined);
    },
    delay,
    timeout,
  );
  return () => {
    cancelled = true;
    cancelStart();
  };
}

export function deferUntilPageSettles(
  task: () => void,
  delay: number,
  timeout: number,
) {
  const idleWindow = window as IdleWindow;
  let delayHandle = 0;
  let idleHandle = 0;
  let cancelled = false;
  let waitingForVisibility = false;
  let finished = false;

  const run = () => {
    idleHandle = 0;
    if (cancelled || finished) return;
    if (document.hidden) {
      waitingForVisibility = true;
      return;
    }
    finished = true;
    task();
  };
  const queueIdle = () => {
    if (cancelled || finished) return;
    if (idleWindow.requestIdleCallback)
      idleHandle = idleWindow.requestIdleCallback(run, { timeout });
    else run();
  };
  const onVisibilityChange = () => {
    if (!document.hidden && waitingForVisibility) {
      waitingForVisibility = false;
      queueIdle();
    }
  };
  const queue = () => {
    delayHandle = window.setTimeout(queueIdle, delay);
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  if (document.readyState === 'complete') queue();
  else window.addEventListener('load', queue, { once: true });

  return () => {
    cancelled = true;
    window.removeEventListener('load', queue);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    if (delayHandle) window.clearTimeout(delayHandle);
    if (idleHandle && idleWindow.cancelIdleCallback)
      idleWindow.cancelIdleCallback(idleHandle);
  };
}
